import { useCallback, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

type ConversationStage = 'ready' | 'typing' | 'reveal';

type ConversationRun = {
  id: number;
  scope: string | null;
  inputKey: string | null;
  stage: ConversationStage;
};

export function useAdvisorConversationMotion({
  inputKey,
  enabled,
  scope,
}: {
  inputKey: string;
  enabled: boolean;
  scope: string | null;
}) {
  const initialReducedMotion = useReducedMotion();
  const [reducedMotion, setReducedMotion] = useState(initialReducedMotion);
  const [run, setRun] = useState<ConversationRun | null>(null);
  const sequence = useRef(0);
  const currentScope = useRef(scope);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  currentScope.current = scope;

  const cancelTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const reset = useCallback(() => {
    cancelTimers();
    setRun(null);
  }, [cancelTimers]);

  const begin = useCallback(() => {
    cancelTimers();
    setRun({
      id: ++sequence.current,
      scope: currentScope.current,
      inputKey: null,
      stage: 'typing',
    });
  }, [cancelTimers]);

  useEffect(() => {
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReducedMotion,
    );
    return () => subscription.remove();
  }, []);

  const runId = run?.id;
  const runScope = run?.scope;
  const runInputKey = run?.inputKey;

  useEffect(() => {
    if (runId === undefined) return;

    if (!enabled || reducedMotion || runScope !== scope) {
      setRun(null);
      return;
    }

    // begin() precedes the answer update. Bind its pending run to the next input.
    if (runInputKey === null) {
      setRun((current) => (current?.id === runId ? { ...current, inputKey } : current));
      return;
    }

    if (runInputKey !== inputKey) {
      setRun(null);
      return;
    }

    const showStage = (stage: ConversationStage) => {
      setRun((current) => (current?.id === runId ? { ...current, stage } : current));
    };

    timers.current = [
      setTimeout(() => showStage('reveal'), 1350),
      setTimeout(() => setRun((current) => (current?.id === runId ? null : current)), 1760),
    ];

    return cancelTimers;
  }, [cancelTimers, enabled, inputKey, reducedMotion, runId, runInputKey, runScope, scope]);

  const active =
    enabled &&
    !reducedMotion &&
    run?.scope === scope &&
    (run.inputKey === null || run.inputKey === inputKey);

  return {
    stage: active ? run.stage : ('ready' as ConversationStage),
    begin,
    reset,
    reducedMotion,
  };
}
